/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react"
import { Info } from "lucide-react"
import { useState, useMemo, useCallback, useRef, forwardRef, type ReactNode } from "react"
import { TableVirtuoso, type Components, type TableVirtuosoHandle } from "react-virtuoso"
import { TableHead } from "./ui/table"
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip"
import { tableStyles } from "../styles/mixins/table"
import { theme, alpha } from "../styles/theme/theme"
import { sortDateColumns } from "../utils/date"
import type { SortCriteria } from "../types/sort"
import { getSortDirection, getSortIndex } from "../types/sort"
import type { Interpolation } from "@emotion/react"
import type { Theme } from "../styles"

// ─── Types ────────────────────────────────────────────────────────────────────

type FlatRow<T> =
  | { kind: "data"; item: T; isExpanded: boolean }
  | { kind: "detail"; item: T }
  | { kind: "detail-multi"; item: T; subKey: string }

export interface ColumnDef {
  key: string
  header: ReactNode
  /** When set, the column header becomes clickable for sorting. Value is the backend column name. */
  sortKey?: string
  /** Native browser tooltip rendered on hover. Used to surface raw-source info on mapping headers (CHO-606 Phase 6). */
  tooltip?: string
  css?: Interpolation<Theme>
}

/** A grouped header row rendered above the main column headers. */
export interface HeaderGroup {
  label: ReactNode
  colSpan: number
  css?: Interpolation<Theme>
}

/**
 * Build ColumnDef[] for time-series tables: label columns → date columns → total.
 * Used by planning views (DemandPlan, ShipmentForecast, SupplyWalk).
 *
 * When `displayBy` is provided, past and current period columns get a light grey
 * background from the theme (pastPeriodBg).
 */
export function buildTimeSeriesColumns(
  labelColumns: ColumnDef[],
  dateColumns: string[],
  options?: { displayBy?: string; pastPeriodSet?: Set<string> },
): ColumnDef[] {
  const pastSet = options?.pastPeriodSet
  const sorted = sortDateColumns(dateColumns)
  return [
    ...labelColumns,
    ...sorted.map((col) => ({
      key: col,
      header: col,
      sortKey: col,
      css: [
        tableStyles.right,
        tableStyles.colFixed,
        ...(pastSet?.has(col) ? [tableStyles.pastPeriodHead] : []),
      ] as Interpolation<Theme>,
    })),
    {
      key: "_total",
      header: "Total",
      sortKey: "total",
      css: [tableStyles.right, tableStyles.thTotal, tableStyles.colMin] as Interpolation<Theme>,
    },
  ]
}

/**
 * Unified virtualized data table with optional expand/collapse.
 *
 * **Flat mode** (no expansion):
 *   Provide `data`, `columns`, `renderCells`. Omit `getRowId`/`renderExpandedContent`.
 *
 * **Expandable mode** (single detail row per item):
 *   Also provide `getRowId` and `renderExpandedContent`. Each row can expand
 *   to show a detail section (single <td colSpan={N}> or arbitrary content).
 *
 * **Multi-detail mode** (multiple detail rows sharing the outer column grid):
 *   Provide `getRowId`, `getDetailKeys`, and `renderDetailRow`. Each expanded
 *   item emits one flat row per sub-key whose cells participate in the outer
 *   table's column sizing — no nested table, zero alignment drift.
 */
export interface DataTableProps<T> {
  /** Rows to display */
  data: T[]
  /** Column header definitions (renders structured <th> elements) */
  columns?: ColumnDef[]
  /** Optional grouped header row rendered above column headers (e.g. "Actuals" / "Forecast" spanning date columns). */
  headerGroups?: HeaderGroup[]
  /** Custom header row (alternative to `columns` for full control) */
  renderHeader?: () => ReactNode
  /** Render the cells for a data row. If expandable, receives toggle function. */
  renderCells: (item: T, isExpanded: boolean, toggle: () => void) => ReactNode
  /**
   * Return a stable unique id for each row. Required for expand/collapse.
   * When omitted, the table is flat (no expand/collapse).
   */
  getRowId?: (item: T) => string
  /**
   * Render the full expanded detail row content (single-detail mode).
   * Typically a single <td colSpan={N}> wrapping a nested table or arbitrary content.
   * Only called for expanded rows. Requires `getRowId` to be set.
   */
  renderExpandedContent?: (item: T) => ReactNode
  /**
   * Return sub-keys for multi-detail expansion. Each key becomes its own flat
   * row in the outer table. Requires `getRowId` and `renderDetailRow`.
   */
  getDetailKeys?: (item: T) => string[]
  /**
   * Render cells for a single detail row (multi-detail mode). Returns <td> cells
   * directly — they participate in the outer table's column grid.
   */
  renderDetailRow?: (item: T, subKey: string) => ReactNode
  /** Custom background per row */
  getRowBg?: (item: T, isExpanded: boolean) => string
  /** Custom background for detail/expanded rows (default: theme.colors.muted) */
  getDetailRowBg?: (item: T) => string
  /** Custom background for multi-detail rows. Receives the sub-key so each detail row can be styled individually. */
  getDetailMultiRowBg?: (item: T, subKey: string) => string
  /** When true, the row gets a blue50 highlight (selection state) */
  isRowSelected?: (item: T) => boolean
  /** Current sort state (for displaying indicators). Uses sortKey from ColumnDef. */
  sort?: SortCriteria[]
  /** Called when a sortable column header is clicked. `multi` is true when shift is held. */
  onSort?: (sortKey: string, multi: boolean) => void
  /** Ref to the inner scroll container (for syncing scroll across tables) */
  scrollerRef?: React.RefObject<HTMLDivElement | null>
  /** Ref to the Virtuoso instance — exposes `scrollToIndex` for keyboard nav
   *  past the rendered viewport. */
  virtuosoRef?: React.RefObject<TableVirtuosoHandle | null>
  /** Scroll handler on the inner scroll container */
  onScrollerScroll?: () => void
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DataTable<T>({
  data,
  columns,
  headerGroups,
  renderHeader,
  renderCells,
  getRowId,
  renderExpandedContent,
  getDetailKeys,
  renderDetailRow,
  getRowBg,
  getDetailRowBg,
  getDetailMultiRowBg,
  isRowSelected,
  sort,
  onSort,
  scrollerRef,
  virtuosoRef,
  onScrollerScroll,
}: DataTableProps<T>) {
  const isMultiDetail = getRowId != null && getDetailKeys != null && renderDetailRow != null
  const isExpandable = getRowId != null && (renderExpandedContent != null || isMultiDetail)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  const toggleExpand = useCallback((id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }, [])

  // Refs so memoized tableComponents always sees latest callbacks
  const isRowSelectedRef = useRef(isRowSelected)
  isRowSelectedRef.current = isRowSelected
  const getRowBgRef = useRef(getRowBg)
  getRowBgRef.current = getRowBg
  const getDetailRowBgRef = useRef(getDetailRowBg)
  getDetailRowBgRef.current = getDetailRowBg
  const getDetailMultiRowBgRef = useRef(getDetailMultiRowBg)
  getDetailMultiRowBgRef.current = getDetailMultiRowBg

  const flatRows = useMemo<FlatRow<T>[]>(() => {
    if (!isExpandable) {
      return data.map((item) => ({ kind: "data" as const, item, isExpanded: false }))
    }
    const rows: FlatRow<T>[] = []
    for (const item of data) {
      const id = getRowId!(item)
      const isExpanded = expandedRows.has(id)
      rows.push({ kind: "data", item, isExpanded })
      if (isExpanded) {
        if (isMultiDetail) {
          for (const subKey of getDetailKeys!(item)) {
            rows.push({ kind: "detail-multi", item, subKey })
          }
        } else {
          rows.push({ kind: "detail", item })
        }
      }
    }
    return rows
  }, [data, expandedRows, getRowId, isExpandable, isMultiDetail, getDetailKeys])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tableComponents = useMemo<Components<FlatRow<T>>>(() => ({
    Scroller: forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
      ({ style, ...props }, ref) => (
        <div
          ref={(el) => {
            // Forward to virtuoso's ref
            if (typeof ref === "function") ref(el);
            else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
            // Expose to parent via scrollerRef
            if (scrollerRef) (scrollerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
          }}
          style={{ ...style, overflow: "auto" }}
          onScroll={onScrollerScroll}
          {...props}
        />
      ),
    ),
    Table: ({ style, ...props }) => (
      <table style={style} css={tableStyles.table} {...props} />
    ),
    TableHead: forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
      (props, ref) => <thead ref={ref} css={tableStyles.thead} {...props} />,
    ),
    TableBody: forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
      (props, ref) => <tbody ref={ref} {...props} />,
    ),
    TableRow: ({ item, ...props }) => {
      const row = item as FlatRow<T>
      if (row.kind === "detail") {
        const bg = getDetailRowBgRef.current?.(row.item) ?? theme.colors.muted
        return <tr {...props} css={css`background: ${bg};`} />
      }
      if (row.kind === "detail-multi") {
        const bg = getDetailMultiRowBgRef.current?.(row.item, row.subKey)
          ?? getDetailRowBgRef.current?.(row.item)
          ?? theme.colors.muted
        return (
          <tr {...props} css={css`
            background: ${bg};
            border-bottom: 1px solid ${theme.colors.border}20;
          `} />
        )
      }
      const selected = isRowSelectedRef.current?.(row.item) ?? false
      const customBg = getRowBgRef.current?.(row.item, row.isExpanded)
      const bg = selected
        ? theme.colors.blue50
        : customBg ?? theme.colors.background
      const hoverBg = selected
        ? theme.colors.blue50
        : customBg
          ? alpha(customBg, 0.8)
          : theme.colors.muted
      return (
        <tr
          {...props}
          css={css`
            border-bottom: 1px solid ${theme.colors.border};
            background: ${bg};
            transition: background-color 0.1s;
            &:hover { background: ${hoverBg}; }
          `}
        />
      )
    },
  }), []) // stable — all callbacks accessed via refs

  const noop = useCallback(() => {}, [])

  // Stable per-row key so react-virtuoso maps content to the correct row when
  // the data reorders (column sort) or the viewport recycles on scroll. Without
  // it virtuoso keys by array index, which can render one row's cells under a
  // different row's label after a re-sort (e.g. a near-empty first receiver
  // showing a neighbour's series). Keyed by the row's stable id; detail rows get
  // a suffix so a data row and its expanded walk rows never collide.
  const computeItemKey = useCallback(
    (index: number, row: FlatRow<T>): string | number => {
      if (getRowId == null) return index
      const id = getRowId(row.item)
      if (row.kind === "detail-multi") return `${id}::d::${row.subKey}`
      if (row.kind === "detail") return `${id}::detail`
      return `${id}::data`
    },
    [getRowId],
  )

  return (
    <TableVirtuoso<FlatRow<T>>
      ref={virtuosoRef}
      style={{ height: "100%" }}
      data={flatRows}
      computeItemKey={computeItemKey}
      fixedHeaderContent={
        renderHeader ??
        (columns
          ? () => (
              <>
              {headerGroups && (
                <tr>
                  {headerGroups.map((g, i) => (
                    <TableHead key={i} colSpan={g.colSpan} css={[tableStyles.th, tableStyles.thGroupRow, g.css]}>
                      {g.label}
                    </TableHead>
                  ))}
                </tr>
              )}
              <tr>
                {columns.map((col) => {
                  const isSortable = !!col.sortKey && !!onSort
                  const dir = col.sortKey && sort ? getSortDirection(sort, col.sortKey) : null
                  const isMulti = sort && sort.length > 1
                  const sortPos = col.sortKey && sort && dir ? getSortIndex(sort, col.sortKey) : -1
                  return (
                    <TableHead
                      key={col.key}
                      css={[
                        tableStyles.th,
                        col.css,
                        isSortable && css`cursor: pointer; user-select: none;`,
                      ]}
                      onClick={isSortable ? (e: React.MouseEvent) => onSort(col.sortKey!, e.shiftKey) : undefined}
                    >
                      {col.header}
                      {col.tooltip && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              css={css`
                                display: inline-flex;
                                align-items: center;
                                margin-left: 0.25rem;
                                color: ${theme.colors.mutedForeground};
                                cursor: help;
                                vertical-align: middle;
                                &:hover {
                                  color: ${theme.colors.foreground};
                                }
                              `}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Info size={12} aria-hidden />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent
                            side="top"
                            css={css`
                              max-width: 22rem;
                              white-space: normal;
                              line-height: 1.4;
                            `}
                          >
                            {col.tooltip}
                          </TooltipContent>
                        </Tooltip>
                      )}
                      {isMulti && sortPos > 0 && (
                        <span css={css`
                          display: inline-block;
                          vertical-align: middle;
                          margin-left: 0.125rem;
                          font-size: 0.5625rem;
                          font-weight: 600;
                          line-height: 1;
                          color: ${theme.colors.mutedForeground};
                        `}>{sortPos}</span>
                      )}
                    </TableHead>
                  )
                })}
              </tr>
              </>
            )
          : () => null)
      }
      components={tableComponents}
      itemContent={(_, row) => {
        if (row.kind === "detail") return renderExpandedContent!(row.item)
        if (row.kind === "detail-multi") return renderDetailRow!(row.item, row.subKey)
        const toggle = isExpandable
          ? () => toggleExpand(getRowId!(row.item))
          : noop
        return renderCells(row.item, row.isExpanded, toggle)
      }}
    />
  )
}
